import ConfirmationDialog from "../shared/ConfirmationDialog";
import EmptyState from "../shared/EmptyState";
import GroupAdd from "./GroupAdd";
import GroupsSearchField from "./GroupsSearchField";
import ParticipantsPagination from "../users/participants/ParticipantsPagination";
import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  Typography
  } from "@mui/material";
import { Delete, Edit, Group } from "@mui/icons-material";
import { GroupOperationResult, GroupWithId } from "../../interfaces/IGroups";
import { ParticipantWithId } from "../../interfaces/IParticipants";
import { useGroupsSearch } from "../../hooks/useGroupsSearch";

interface GroupsListProps {
  groups: GroupWithId[];
  participants: ParticipantWithId[];
  onEdit: (groupId: string) => void;
  onDelete: (groupId: string) => Promise<GroupOperationResult>;
  onAdd: () => void;
}

export default function GroupsList({
  groups,
  participants,
  onEdit,
  onDelete,
  onAdd,
}: GroupsListProps) {
  // Hook do wyszukiwania
  const { searchTerm, setSearchTerm, filteredGroups } = useGroupsSearch(groups, participants);

  // Paginacja
  const [rowsPerPage, setRowsPerPage] = useState<number>(15);
  const [page, setPage] = useState<number>(0);

  const pagedGroups = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredGroups.slice(start, start + rowsPerPage);
  }, [filteredGroups, page, rowsPerPage]);

  const handleChangeRowsPerPage = useCallback((value: number) => {
    setRowsPerPage(value);
    setPage(0);
  }, []);

  const handleChangePage = useCallback((value: number) => {
    setPage(value);
  }, []);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<GroupWithId | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (group: GroupWithId) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!groupToDelete) return;

    setIsDeleting(true);
    const result = await onDelete(groupToDelete.id);
    setIsDeleting(false);

    if (result.success) {
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
    } else {
      console.error("Błąd podczas usuwania grupy:", result.error);
      // Można dodać snackbar z błędem
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setGroupToDelete(null);
  };

  const getParticipantCount = (group: GroupWithId): number => {
    return group.participantIds.length;
  };

  if (groups.length === 0) {
    return (
      <Box
        sx={{
          border: "1px dashed #ccc",
          borderRadius: 1,
          p: 2,
        }}
      >
        <EmptyState message="Brak grup. Utwórz pierwszą grupę, aby rozpocząć." />
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <GroupAdd onClick={onAdd} disabled={false} />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header listy grup */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Group sx={{ color: "#6366f1", fontSize: 20 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#2f2f2f" }}>
          Lista grup ({groups.length})
        </Typography>
      </Box>

      {/* Lista z wyszukiwaniem i paginacją - zintegrowana */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1,
          border: "1px solid #ddd",
          overflow: "hidden",
        }}
      >
        {/* Przycisk dodawania grupy - nad wyszukiwarką */}
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 1 }}>
          <GroupAdd onClick={onAdd} disabled={false} />
        </Box>

        <List sx={{ mx: 0, px: 0 }}>
          {/* Pole wyszukiwania */}
          <GroupsSearchField searchTerm={searchTerm} onSearchChange={setSearchTerm} />

          {/* Lista grup (stronicowana) */}
          {pagedGroups.map((group, index) => (
            <React.Fragment key={group.id}>
              <ListItem
                sx={{ py: 2, px: { xs: 2, sm: 3 }, "&:hover": { backgroundColor: "#f9f9f9" } }}
              >
                <Grid container spacing={2} sx={{ width: "100%" }}>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Group sx={{ color: "#6366f1", fontSize: 20 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, minWidth: "200px" }}>
                        {group.name}
                      </Typography>
                      <Chip
                        label={`${getParticipantCount(group)} zawodników`}
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 500, height: 24, fontSize: "0.75rem" }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: { xs: "flex-start", md: "flex-end" },
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => onEdit(group.id)}
                        sx={{
                          color: "#6366f1",
                          borderColor: "#6366f1",
                          "&:hover": {
                            backgroundColor: "#f1e7de",
                            borderColor: "#6366f1",
                          },
                          py: 0.5,
                          px: 1.5,
                          fontSize: "0.7rem",
                        }}
                      >
                        Edytuj
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Delete />}
                        onClick={() => handleDeleteClick(group)}
                        disabled={isDeleting}
                        sx={{
                          color: "#ef4444",
                          borderColor: "#ef4444",
                          "&:hover": {
                            backgroundColor: "#fef2f2",
                            borderColor: "#ef4444",
                          },
                          py: 0.5,
                          px: 1.5,
                          fontSize: "0.7rem",
                        }}
                      >
                        Usuń
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </ListItem>
              {index < pagedGroups.length - 1 && <Divider />}
            </React.Fragment>
          ))}

          {/* Komunikat o braku wyników wyszukiwania */}
          {searchTerm.trim() && filteredGroups.length === 0 && (
            <Box
              sx={{
                bgcolor: "#fff",
                px: { xs: 2, sm: 3 },
                py: 3,
                textAlign: "center",
              }}
            >
              <Typography color="text.secondary" sx={{ fontSize: "0.9rem" }}>
                Nie znaleziono grup pasujących do wyszukiwania: "{searchTerm}"
              </Typography>
            </Box>
          )}
        </List>

        {/* Paginacja - zintegrowana z listą */}
        <ParticipantsPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={filteredGroups.length}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>

      <ConfirmationDialog
        open={deleteDialogOpen}
        title="Potwierdź usunięcie grupy"
        message={
          groupToDelete
            ? `Czy na pewno chcesz usunąć grupę "${groupToDelete.name}"? Tej operacji nie można cofnąć.`
            : ""
        }
        cancelText="Anuluj"
        confirmText="Usuń"
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        confirmColor="error"
      />
    </Box>
  );
}

